import { z } from 'zod';
import type { MarcaRevisaoResponse } from './lotes.js';
import { itemTipo, type ItemTipo } from './itens.js';
import { cuid, isoDate } from './primitives.js';
import type { SimplesResumoResponse } from './simples-apuracao.js';
import { UFS, type OrigemLucro } from './tabelas-preco.js';

const bool = z.enum(['true', 'false']).transform((v) => v === 'true');

/** GET /precificacao/lista. Paginacao por cursor (opaco) com limite maximo de 200 linhas por pagina. */
export const listaPrecosSchema = z.object({
  /** Data que resolve as vigencias. Default: hoje (UTC). */
  data: isoDate.optional(),
  /** Uma tabela; ausente = todas as tabelas ativas com vigencia na data. */
  tabelaId: cuid.optional(),
  /** Condicao de pagamento; ausente = a condicao default da vigencia de cada tabela. */
  condicaoId: cuid.optional(),
  /** Sessao 51 (tabela ESTRUTURADA): o sugerido e calculado para esta UF de destino e condicao de contribuinte (so leitura; o preco praticado continua unico). Ausentes = UF de origem, contribuinte. */
  ufDestino: z.enum(UFS).optional(),
  contribuinte: bool.optional(),
  tipo: itemTipo.optional(),
  /** Categoria do item (com as subcategorias) ou `SEM` (Sessao 28). */
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
  /** Busca por codigo ou descricao (contem, sem diferenciar caixa). */
  q: z.string().trim().min(1).max(100).optional(),
  /** Praticado existe e e menor que o sugerido (comparado ao sugerido arredondado a 2 casas, como exibido). */
  abaixoDoSugerido: bool.optional(),
  /** Nunca houve preco praticado para o item x tabela x condicao. */
  semPraticado: bool.optional(),
  /** Item sem ficha tecnica escolhida. */
  semFicha: bool.optional(),
  /** Sessao 11: inclui, em cada linha, o preco do RASCUNHO (nao publicado) e seu lucro. So para quem tem `praticado.gravar` (os demais ignoram). */
  rascunho: bool.optional(),
  /** Ordem dos itens (dentro de cada tabela): por codigo, crescente ou decrescente. Outras colunas sao calculadas so na pagina. */
  ordem: z.enum(['codigo', '-codigo']).default('codigo'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().min(1).max(300).optional(),
});
export type ListaPrecosQuery = z.infer<typeof listaPrecosSchema>;

/** GET /precificacao/lista.csv: mesmos filtros da lista, sem paginacao (o servidor percorre as paginas). */
export const listaPrecosCsvSchema = listaPrecosSchema.omit({ limit: true, cursor: true });
export type ListaPrecosCsvQuery = z.infer<typeof listaPrecosCsvSchema>;

/** Preco do rascunho na condicao listada: digitado (BASE), fixado (MANUAL) ou derivado da base (DERIVADO). */
export interface RascunhoDaLinha {
  preco: string;
  origem: 'BASE' | 'MANUAL' | 'DERIVADO';
  lucroSimulado: string;
  faixa: 'green' | 'orange' | 'red';
  abaixoDoMinimo: boolean;
}

/** Margem de contribuicao da linha (Sessao 15): do praticado quando ha, senao do sugerido. `valor` (R$) so com custo.ver. */
export interface ListaMc {
  /** Fracao (MC / preco sem IPI); null se o preco e zero. */
  pct: string | null;
  faixa: 'GREEN' | 'ORANGE' | 'RED';
  base: 'PRATICADO' | 'SUGERIDO';
  valor?: string;
  /** Preco sem IPI da linha (a base da MC%). Usado pelo ponto de equilibrio; a projecao nao o devolve a quem nao ve custo. */
  receita?: string;
}

/** Reforma tributaria na linha (Sessao 15, 6b): preco sem tributos por fora, "+ CBS/IBS" e total da NF, na data da lista. */
export interface ListaReforma {
  regime: 'LEGACY' | 'DUAL_2026' | 'TRANSICAO' | 'NOVO_2033';
  etapa: string;
  /** 2026: CBS/IBS destacados, nao somam ao total. */
  informativo: boolean;
  /** Aliquota de referencia estimada (hipotetica) em uso. */
  aliquotaEstimada: boolean;
  precoSemTributos: string;
  cbsIbs: string;
  totalNf: string;
  praticadoSemTributos?: string;
  praticadoCbsIbs?: string;
  praticadoTotalNf?: string;
}

export interface ListaPrecosLinha {
  /** Rascunho nao publicado desta linha (so com `?rascunho=true` e praticado.gravar). */
  rascunho?: RascunhoDaLinha;
  /** Posicao na ordem de emissao da requisicao (une `itens` e `skipped` na ordem da lista). */
  posicao: number;
  itemId: string;
  codigo: string;
  descricao: string;
  tipo: ItemTipo;
  unidade: string;
  tabelaId: string;
  tabelaCodigo: string;
  condicaoId: string;
  /** Sessao 11: a condicao da linha e a BASE da tabela (as outras derivam dela). */
  condicaoBase?: boolean;
  /**
   * Os campos marcados "so com custo.ver" sao OMITIDOS da resposta (nao vem null) para quem nao tem a permissao
   * (VENDEDOR, LEITURA): custo, origem do custo, ficha, preco a vista, lucro de precisao cheia, "sem ficha" e warnings.
   */
  vigenciaId?: string;
  /** So com custo.ver. */
  unitCost?: string;
  /** So com custo.ver. */
  costSource?: 'ficha' | 'aquisicao' | 'composicao';
  /** So com custo.ver. */
  fichaId?: string | null;
  /** precoComIpi a prazo (saida do motor, precisao cheia). */
  sugerido: string;
  /** `sugerido` arredondado para exibir/negociar (2 casas, meio para cima). */
  sugeridoExibicao: string;
  /** So com custo.ver. */
  sugeridoAVista?: string;
  /** Ultimo PrecoPraticado (item x tabela x condicao) ou null. */
  praticado: string | null;
  praticadoEm: string | null;
  /** Sessao 51b: com UF de destino, de onde vem o praticado: excecao MANUAL da UF ou DERIVADO do preco da UF de origem (`derivadoDe`, ex.: "SP"). */
  praticadoUf?: { origem: 'MANUAL' | 'DERIVADO'; derivadoDe: string | null };
  /** Sessao 52b, so com custo.ver: Simples com efetiva CALCULADA (anexo, faixa, RBT12...). Ausente na efetiva digitada e fora do Simples. */
  simples?: SimplesResumoResponse;
  /** simulatedProfit do motor (fracao) ou null sem praticado. So com custo.ver (os demais usam `lucroSimuladoExibicao`). */
  lucroSimulado?: string | null;
  /** `lucroSimulado` arredondado para exibir (fracao, 4 casas, meio para cima). */
  lucroSimuladoExibicao: string | null;
  lucroDesejado: string;
  /** Lucro minimo resolvido (tabela > padrao da empresa; fracao). */
  lucroMinimo: string;
  /** De onde vieram meta e minimo (DP6): da tabela ou do padrao da empresa. */
  lucroDesejadoOrigem: OrigemLucro;
  lucroMinimoOrigem: OrigemLucro;
  /** Sessao 15: margem de contribuicao (MC%), ordenavel na tela. */
  mc?: ListaMc;
  /** Sessao 15 (6b): regime da reforma e total da NF. */
  reforma?: ListaReforma;
  /** Faixa do lucro simulado: verde >= desejado, laranja >= minimo, vermelho abaixo (mesma regra do orcamento). Null sem praticado. */
  faixa: 'green' | 'orange' | 'red' | null;
  /** `semFicha` so com custo.ver. */
  flags: { abaixoDoSugerido: boolean; semPraticado: boolean; semFicha?: boolean; abaixoDoMinimo: boolean };
  /** So com custo.ver (os warnings do motor trazem divisor e limiares). */
  warnings?: unknown[];
  /**
   * So com custo.ver e so quando ha: marca "CPM antigo (mes)" da ficha usada (centros sem CPM no ultimo mes salvo, com a
   * competencia que valeu). Subfichas incluidas.
   */
  cpmAntigo?: Array<{ centroCodigo: string; competencia: string }>;
  /**
   * "Custo atualizado - revisar preco" (E10): ha marca aberta nesta chave (o sugerido mudou por custo e o praticado nao).
   * Todos com `lista.ver` recebem a marca e o novo sugerido; VENDEDOR/LEITURA sem lucro nem origem. Ausente quando nao ha marca.
   */
  marcaRevisao?: MarcaRevisaoResponse;
}

export interface ListaPrecosPulado {
  posicao: number;
  itemId: string;
  codigo: string;
  descricao: string;
  tipo: ItemTipo;
  tabelaId: string;
  tabelaCodigo: string;
  reason: 'UNIT_COST_MISSING' | 'TAXES_MISSING' | 'LUCRO_DESEJADO_AUSENTE';
  /**
   * Quando o custo da ficha escolhida nao pode ser resolvido na data: `CICLO_NA_FICHA`, `PROFUNDIDADE_FICHA_EXCEDIDA` (ficha
   * em ciclo ou com mais de 10 niveis) ou `CPM_AUSENTE` (centro sem CPM ate o mes da data) ou `AQUISICAO_AUSENTE` (insumo da ficha sem custo de aquisicao vigente na data; para o proprio item comprado sem aquisicao a linha vem pulada com `UNIT_COST_MISSING` sem motivoCusto). Nesses casos `reason` e
   * `UNIT_COST_MISSING`. Sem custo.ver (a lista nao devolve o motivo) a chave nao existe.
   */
  motivoCusto?: 'CICLO_NA_FICHA' | 'PROFUNDIDADE_FICHA_EXCEDIDA' | 'CPM_AUSENTE' | 'AQUISICAO_AUSENTE' | 'SAIDA_INVALIDA';
  /** Omitida para quem nao tem custo.ver: so o `reason`. */
  message?: string;
}

export interface ListaPrecosResponse {
  referenciaEm: string;
  itens: ListaPrecosLinha[];
  skipped: ListaPrecosPulado[];
  /** Tabelas que nao puderam ser calculadas (sem condicao default, condicao fora da tabela...). */
  tabelasIgnoradas: Array<{ tabelaId: string; codigo: string; code: string; message: string }>;
  /** Cursor da proxima pagina; null = fim. */
  nextCursor: string | null;
  /** Pares item x tabela enviados ao motor nesta requisicao (so os da pagina; mais com abaixoDoSugerido). */
  calculados: number;
  motorVersao: string;
}

/** GET /precificacao/lista-vendedores (S30-0a): só preço PUBLICADO, vigente na data e liberado no escopo do item (tabela x condição). Sem sugerido, custo nem lucro. */
export const listaVendedoresSchema = z.object({
  data: isoDate.optional(),
  tabelaId: cuid.optional(),
  tipo: itemTipo.optional(),
  categoriaId: z.union([cuid, z.literal('SEM')]).optional(),
  q: z.string().trim().min(1).max(100).optional(),
});
export type ListaVendedoresQuery = z.infer<typeof listaVendedoresSchema>;
export interface ListaVendedoresItem {
  itemId: string;
  codigo: string;
  descricao: string;
  unidade: string;
  /** condicaoId -> preço publicado vigente e liberado (casas da empresa); null = sem preço nesta condição. */
  precos: Record<string, string | null>;
}
export interface ListaVendedoresTabela {
  tabelaId: string;
  codigo: string;
  nome: string;
  condicoes: Array<{ id: string; nome: string }>;
  itens: ListaVendedoresItem[];
}
export interface ListaVendedoresSemPreco {
  tabelaCodigo: string;
  itemCodigo: string;
  itemDescricao: string;
  motivo: 'SEM_PRECO_PUBLICADO' | 'FORA_DO_ESCOPO';
}
export interface ListaVendedoresResponse {
  referenciaEm: string;
  tabelas: ListaVendedoresTabela[];
  /** Rodapé "itens sem preço publicado": só vem para quem tem praticado.gravar (o equivalente de lista.editar). */
  semPrecoPublicado?: ListaVendedoresSemPreco[];
}
